import { message } from "antd"
import axios from "axios"
import i18n from "../services/i18n"

/*
  Backend {message: "CODE"} qaytaradi (masalan THE_QUANTITY_OF_PRODUCTS_IS_NOT_ENOUGH).
  Kod ko'rinishidagi matnni foydalanuvchi tiliga o'giramiz — aks holda ekranda
  xom "THE_QUANTITY..." chiqardi (guruh: menejer sotuvida uch xil toast).
*/
const looksLikeCode = (s: string) => /^[A-Z][A-Z0-9_]{3,}$/.test(s)
const translateCode = (s: string): string => {
  if (looksLikeCode(s)) {
    const translated = i18n.t(s)
    if (translated && translated !== s) return String(translated)
  }
  return s
}

const production = process.env.NODE_ENV === "production"
/*
  window.__API_URL__ — sinov uchun manzilni almashtirish imkoni (mobil
  klientdagi kabi): brauzer-testda tayyor build lokal API ga ulanadi.
  Oddiy foydalanuvchida bu o'zgaruvchi hech qachon o'rnatilmaydi.
*/
export const API_URL =
  (typeof globalThis !== "undefined" && (globalThis as any).__API_URL__) ||
  (production
    ? process.env.REACT_APP_API_URL || "https://apicrp.zendig.uz/api/"
    : (process.env.REACT_APP_API_URL || "http://localhost:5000/api/"))
export const SITE_URL = process.env.REACT_APP_SITE_URL || API_URL.replace(/\/api\/?$/, "") + "/"
export const WS_URL = production
  ? process.env.REACT_APP_WS_URL || "wss://apicrp.zendig.uz/"
  : "ws://localhost:5000/"
export const MEDIA_URL = SITE_URL + "media/"
export const MINIO_URL = "https://minio.ittec.uz/erp-crm/"

export const getFullImageUrl = (photo?: string | null): string => {
  if (!photo) return ""
  if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("data:")) {
    return photo
  }
  return `${MINIO_URL}${photo}`
}
/** Backend xatosidan odam o'qiydigan MATN ajratish (hech qachon obyekt emas) */
export const extractErrorText = (error: any): string => {
  const data = error?.response?.data
  if (typeof data === "string" && data.trim()) return translateCode(data.trim())
  if (data && typeof data === "object") {
    // {message: "..."} — bizning kontrakt (ko'pincha xato KODI)
    if (typeof data.message === "string" && data.message.trim()) return translateCode(data.message.trim())
    // {error: "..."} — ba'zi kontrollerlar (masalan ReturnSales) shu shaklda
    // qaytaradi. Ilgari o'qilmasdi va toastda tarjimasiz umumiy matn chiqardi.
    if (typeof data.error === "string" && data.error.trim()) return translateCode(data.error.trim())
    // {title: "..."} — ASP.NET ProblemDetails
    if (typeof data.title === "string" && data.title.trim()) return translateCode(data.title.trim())
    // {errors: {field: ["..."]}} — model validatsiyasi
    if (data.errors && typeof data.errors === "object") {
      const first: any = Object.values(data.errors)[0]
      if (Array.isArray(first) && typeof first[0] === "string") return first[0]
      if (typeof first === "string") return first
    }
    // message ichida obyekt bo'lsa ham matnga tushiramiz
    if (data.message && typeof data.message === "object") {
      const m: any = data.message
      if (typeof m.title === "string") return translateCode(m.title)
    }
  }
  if (error?.response?.status === 403) return String(i18n.t("noPermission") || "Bu amal uchun huquqingiz yo'q")
  if (error?.response?.status === 401) return String(i18n.t("sessionExpired") || "Avtorizatsiya muddati tugagan")
  return error?.message || String(i18n.t("errorOccurred") || "Xatolik yuz berdi")
}
/*
  MUHIM: backend ruxsat RAD etilganda ham 401 qaytaradi (403 emas).
  Shuning uchun bitta 401 = sessiya tugadi degani EMAS. Sessiya faqat
  yengil AccountAPI/Me so'rovi ham 401 qaytarsagina tugatiladi — aks
  holda huquqi yo'q bitta so'rov butun sessiyani o'chirib yuborardi
  (menejer kirishi bilan chiqib ketish xatosi shu edi).
*/
let checkingSession = false
const handleUnauthorized = async () => {
  if (checkingSession) return
  checkingSession = true
  try {
    const tok = localStorage.getItem("accessToken")
    if (!tok) return
    const r = await fetch(API_URL + "AccountAPI/Me", {
      headers: { Authorization: "Bearer " + tok },
    })
    if (r.status !== 401) return // token tirik — bu ruxsat masalasi, sessiya emas
    try {
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("user")
      localStorage.removeItem("allowed_modules")
      localStorage.removeItem("is_super_admin")
    } catch (e) {
      /* localStorage bo'lmasa ham davom etamiz */
    }
    message.error(String(i18n.t("sessionExpired") || "Sessiya tugadi, qayta kiring"))
    if (!window.location.pathname.includes("/login")) {
      setTimeout(() => {
        window.location.href = "/login"
      }, 400)
    }
  } catch (e) {
    /* tarmoq uzilishi — sessiyaga tegmaymiz */
  } finally {
    checkingSession = false
  }
}

/*
  Token yangilash (2026-10-09). Kirish tokeni 30 soat yashaydi; ilgari
  veb uni faqat sahifa yangilanganda (layout) yangilardi — ochiq tabda
  30 soatdan keyin har so'rov 401 bo'lib, foydalanuvchi chiqib ketardi.
  Endi 401 kelganda avval RenewAccessToken (yangilash tokeni 7 kun) bilan
  yangi juft olinadi va so'rov BIR MARTA qayta yuboriladi. Bir vaqtda bitta
  yangilash (single-flight).
*/
let renewPromise: Promise<string | null> | null = null
const tryRenewWeb = (): Promise<string | null> => {
  if (renewPromise) return renewPromise
  renewPromise = (async () => {
    try {
      const accessToken = localStorage.getItem("accessToken")
      const refreshToken = localStorage.getItem("refreshToken")
      if (!accessToken || !refreshToken) return null
      const r = await fetch(API_URL + "AccountAPI/RenewAccessToken", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, refreshToken }),
      })
      if (!r.ok) return null
      const j: any = await r.json()
      const a = j?.accessToken || j?.AccessToken
      const rt = j?.refreshToken || j?.RefreshToken
      if (!a || !rt) return null
      localStorage.setItem("accessToken", a)
      localStorage.setItem("refreshToken", rt)
      return a
    } catch (e) {
      return null
    } finally {
      renewPromise = null
    }
  })()
  return renewPromise
}

const client = axios.create({ baseURL: API_URL })
/**
 * Xato toastini BIR MARTA ko'rsatish. request() axios xatosini o'zi
 * tarjima qilib ko'rsatadi (401 va silent bundan mustasno — ular ataylab
 * jim). Sahifalar ilgari o'sha xatoni String(error) bilan yana chiqarardi:
 * ikkinchi, tarjimasiz "AxiosError: Request failed..." toasti.
 */
export const showErrorOnce = (err: any) => {
  if (err?.isAxiosError) return
  message.error(extractErrorText(err))
}

export const request = ({ ...options }) => {
  let auth = localStorage.getItem("accessToken")
    ? `Bearer ${localStorage.getItem("accessToken")}`
    : ""
  client.defaults.headers.common.Authorization = auth
  client.defaults.headers.common.Accept = "application/json"

  const onSuccess = (response: any) => {
    return response
  }
  const onError = async (error: any) => {
    /*
      Token muddati tugasa (401), sessiyani tozalab login sahifasiga
      qaytaramiz. Ilgari handler yo'q edi: refetchInterval har 10 soniyada
      401 qaytarib, ekran "401" toastlari bilan to'lardi (guruh: logistika
      sahifasida o'nlab xato). Login so'rovining o'zi bundan mustasno.
    */
    const isLoginReq = options.url?.includes("AccountAPI/Login")
    if (error?.response?.status === 401 && !isLoginReq) {
      // Avval yangilashga urinamiz; muvaffaqiyatli bo'lsa so'rov bir marta qaytariladi
      const isRenewReq = options.url?.includes("RenewAccessToken")
      if (!isRenewReq && !options.__retried) {
        const fresh = await tryRenewWeb()
        if (fresh) {
          return request({
            ...options,
            __retried: true,
            headers: { ...(options.headers || {}), Authorization: "Bearer " + fresh },
          })
        }
      }
      // Sessiya haqiqatan tugagan bo'lsa o'zi login sahifasiga qaytaradi;
      // ruxsat-401 bo'lsa jim qoladi (toast bilan bosmaymiz — ba'zi
      // ma'lumotnoma so'rovlari fon rejimida ketadi)
      handleUnauthorized()
      return Promise.reject(error)
    }
    /*
      Xato matni HAR DOIM string bo'lishi shart. Ilgari data OBYEKT bo'lsa
      (ASP.NET validatsiya xatosi {errors, title} yoki {message: {...}}),
      toastda "[object Object]" chiqardi — menejer login'ida ko'rilgan.
    */
    const errorMsg = extractErrorText(error)
    // silent: fon rejimida ketadigan so'rovlar (masalan AI konteksti) xato toastlari
    // bilan ekranni bosmasligi uchun
    if (!options.silent && !isLoginReq) {
      message.error(errorMsg)
    }
    return Promise.reject(error)
  }
  const { silent, __retried, ...axiosOptions } = options
  return client(axiosOptions).then(onSuccess).catch(onError)
}
